/**
 * ChatMessages - Message list rendering for Deborah Chat
 */
import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { 
  Loader2, 
  User, 
  Bot,
  UserCircle2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  WifiOff
} from 'lucide-react';

const ChatMessages = forwardRef(({ 
  messages, 
  loading, 
  connectionStatus, 
  retryCount,
  isTyping,
  onReconnect,
  onRetryMessage 
}, ref) => {
  
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getMessageIcon = (role) => {
    switch (role) {
      case 'assistant':
        return <Bot className="w-4 h-4" />;
      case 'human_agent':
        return <UserCircle2 className="w-4 h-4" />;
      case 'system':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getMessageStyle = (msg) => {
    const { role, status, type } = msg;
    
    if (status === 'failed') {
      return 'bg-red-500/10 text-red-600 border border-red-500/30';
    }
    
    if (role === 'system') {
      if (type === 'error') {
        return 'bg-red-500/10 text-red-600 text-center';
      }
      return 'bg-orange-500/10 text-orange-700 dark:text-orange-400 text-center';
    }
    
    switch (role) {
      case 'assistant':
        return 'bg-emerald-500/10 text-foreground';
      case 'human_agent':
        return 'bg-blue-500/10 text-foreground';
      default:
        return `bg-primary text-primary-foreground ml-auto ${status === 'sending' ? 'opacity-70' : ''}`;
    }
  };

  // Loading state
  if (loading && !messages.length) {
    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          <p className="text-sm text-muted-foreground">Connecting to Deborah...</p>
          {retryCount > 0 && (
            <p className="text-xs text-amber-600">Retry attempt {retryCount}...</p>
          )}
        </div>
      </div>
    );
  }

  // Disconnected state
  if (connectionStatus === 'disconnected' && !messages.length) {
    return (
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <WifiOff className="w-12 h-12 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground text-center">
            Unable to connect. Please check your internet connection.
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onReconnect}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/30">
      {messages.map((msg) => (
        <motion.div
          key={msg.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div className={`max-w-[85%] ${msg.role === 'system' ? 'w-full' : ''}`}>
            {msg.role !== 'user' && msg.role !== 'system' && (
              <div className="flex items-center gap-1.5 mb-1 text-xs text-muted-foreground">
                {getMessageIcon(msg.role)}
                <span>
                  {msg.role === 'assistant' ? 'Deborah' : 
                   msg.role === 'human_agent' ? (msg.agent_name || 'Support Agent') : ''}
                </span>
              </div>
            )}
            <div className={`px-4 py-2.5 rounded-2xl text-sm ${getMessageStyle(msg)}`}>
              {msg.content}
              {msg.status === 'sending' && (
                <Loader2 className="w-3 h-3 ml-2 inline animate-spin" />
              )}
            </div>
            
            {/* Failed message retry button */}
            {msg.status === 'failed' && (
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-red-500">{msg.error || 'Failed to send'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRetryMessage(msg)}
                  className="h-6 px-2 text-xs text-red-500 hover:text-red-600"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Retry
                </Button>
              </div>
            )}
            
            <p className={`text-[10px] text-muted-foreground mt-1 ${msg.role === 'user' ? 'text-right' : ''}`}>
              {formatTime(msg.created_at)}
              {msg.status === 'sent' && (
                <CheckCircle2 className="w-3 h-3 ml-1 inline text-emerald-500" />
              )}
            </p>
          </div>
        </motion.div>
      ))}
      
      {/* Typing Indicator */}
      {isTyping && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2"
        >
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Bot className="w-4 h-4" />
            <span>Deborah</span>
          </div>
          <div className="px-4 py-3 rounded-2xl bg-emerald-500/10">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        </motion.div>
      )}
      
      <div ref={ref} />
    </div>
  );
});

ChatMessages.displayName = 'ChatMessages';

export default ChatMessages;
